import React, { useState } from 'react';
import { HttpError } from '../../../core/services/HttpService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import ChangeRequestService, { ChangeRequest } from '../services/ChangeRequestService';
import TimeTrackingService from '../services/TimeTrackingService';
import { toast as toastNotify } from 'react-toastify';
import { DateRange } from './DateRangeFilter';
import { TimeTracking, toUTCIfNaiveLocal, extractTimeFromISO, extractDateFromISO, getRequestedWAId, calcBreakMinutesNumber } from './useTimeTrackings';

interface UseCRModalArgs {
  timeTrackings: TimeTracking[];
  uiLedger: Record<number, { originalStart: string; originalEnd: string }>;
  persistLedger: (next: Record<number, { originalStart: string; originalEnd: string }>) => void;
  setApprovedCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setPendingCrMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  setRestoredOriginalMap: React.Dispatch<React.SetStateAction<Record<number, { start: string; end: string }>>>;
  markCrPresence: (ttId: number, has: boolean) => void;
  dateRange: DateRange | null;
  selectedWAs: number[];
  activeEmployeeId: number | null;
  loadTimeTrackings: (range: DateRange, waIds: number[], employeeId?: number | null) => Promise<void>;
}

export function useCRModal({
  timeTrackings, uiLedger, persistLedger,
  setApprovedCrMap, setPendingCrMap, setRestoredOriginalMap,
  markCrPresence, dateRange, selectedWAs, activeEmployeeId, loadTimeTrackings,
}: UseCRModalArgs) {
  const [crModalMode, setCrModalMode] = useState<'normal' | 'fallback'>('normal');
  const [crViewModalVisible, setCrViewModalVisible] = useState(false);
  const [crViewLoading, setCrViewLoading] = useState(false);
  const [crViewError, setCrViewError] = useState('');
  const [crExisting, setCrExisting] = useState<ChangeRequest | null>(null);
  const [crEditMode, setCrEditMode] = useState(false);
  const [crEditError, setCrEditError] = useState('');
  const [crConflicts, setCrConflicts] = useState<any[]>([]);
  const [crEditStartDate, setCrEditStartDate] = useState('');
  const [crEditEndDate, setCrEditEndDate] = useState('');
  const [crEditStartTime, setCrEditStartTime] = useState('');
  const [crEditEndTime, setCrEditEndTime] = useState('');
  const [crEditNotes, setCrEditNotes] = useState('');
  const [crEditBreakMinutes, setCrEditBreakMinutes] = useState('');
  const [crWorkAreaId, setCrWorkAreaId] = useState<number | null>(null);
  const [crSourceTT, setCrSourceTT] = useState<TimeTracking | null>(null);

  const resetCrState = () => {
    setCrViewError(''); setCrViewLoading(true); setCrEditMode(false);
    setCrEditError(''); setCrConflicts([]); setCrViewModalVisible(true);
  };

  const openCRApprovedModal = async (ttId: number) => {
    resetCrState();
    try {
      const tt = timeTrackings.find(t => t.id === ttId) || null;
      setCrSourceTT(tt);
      if (uiLedger[ttId]) {
        setCrModalMode('fallback');
        const { originalStart, originalEnd } = uiLedger[ttId];
        setCrExisting({
          status: 'APPROVED',
          original_start_time: originalStart, original_end_time: originalEnd,
          new_start_time: tt?.start_time, new_end_time: tt?.end_time,
        } as any);
        setCrViewLoading(false);
        return;
      }
      setCrModalMode('normal');
      let cr = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'APPROVED');
      if (!cr) cr = await ChangeRequestService.fetchByTimeTrackingId(ttId);
      setCrExisting(cr);
    } catch { setCrViewError('Failed to load change request'); } finally { setCrViewLoading(false); }
  };

  const handleReverseClick = async (ttId: number) => {
    const ledger = uiLedger[ttId];
    if (ledger) {
      try {
        await TimeTrackingService.updateTimeTracking(ttId, { start_time: ledger.originalStart, end_time: ledger.originalEnd } as any);
        const next = { ...uiLedger }; delete next[ttId]; persistLedger(next);
        setApprovedCrMap(prev => { const n = { ...prev } as any; delete n[ttId]; return n; });
        setPendingCrMap(prev => { const n = { ...prev } as any; delete n[ttId]; return n; });
        setRestoredOriginalMap(prev => { const n = { ...prev } as any; delete n[ttId]; return n; });
        markCrPresence(ttId, false);
        if (dateRange) await loadTimeTrackings(dateRange, selectedWAs, activeEmployeeId);
        return;
      } catch { toastNotify.error('Reverse (Fallback) fehlgeschlagen'); }
    }
    try {
      let cr = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'APPROVED');
      if (!cr) cr = await ChangeRequestService.fetchByTimeTrackingId(ttId);
      if (cr?.id) { await ChangeRequestService.reopen(cr.id); }
      setApprovedCrMap(prev => { const next = { ...prev } as any; delete next[ttId]; return next; });
      markCrPresence(ttId, true);
      try {
        const pcr = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'PENDING');
        if (pcr) {
          const startRaw = (pcr as any)?.new_start_time || (pcr as any)?.requested_start_time || (pcr as any)?.proposed_start_time || (pcr as any)?.original_start_time || (pcr as any)?.original?.start_time;
          const endRaw = (pcr as any)?.new_end_time || (pcr as any)?.requested_end_time || (pcr as any)?.proposed_end_time || (pcr as any)?.original_end_time || (pcr as any)?.original?.end_time;
          const start = toUTCIfNaiveLocal(startRaw);
          const end = toUTCIfNaiveLocal(endRaw);
          if (start && end) { setPendingCrMap(prev => ({ ...prev, [ttId]: { start, end } })); }
        }
      } catch {}
    } catch { toastNotify.error('Reverse failed'); }
  };

  const openCRViewModal = async (ttId: number) => {
    resetCrState();
    try {
      const tt = timeTrackings.find(t => t.id === ttId) || null;
      setCrSourceTT(tt);
      let cr = await ChangeRequestService.fetchByTimeTrackingId(ttId, 'PENDING');
      if (!cr) { cr = await ChangeRequestService.fetchByTimeTrackingId(ttId); }
      setCrExisting(cr);
      markCrPresence(ttId, !!cr);
      if (cr) {
        const startIso = (cr as any).new_start_time || (tt ? tt.start_time : '');
        const endIso = (cr as any).new_end_time || (tt ? tt.end_time : '');
        setCrEditStartDate(extractDateFromISO(startIso));
        setCrEditEndDate(extractDateFromISO(endIso));
        setCrEditStartTime(extractTimeFromISO(startIso));
        setCrEditEndTime(extractTimeFromISO(endIso));
        setCrWorkAreaId(getRequestedWAId(cr) || (tt ? (tt as any).work_area : null) || null);
        setCrEditNotes(cr.employee_notes || '');
        const breakMin = cr.proposed_break_duration ?? cr.original_break_duration ?? (tt ? calcBreakMinutesNumber(tt) : null);
        setCrEditBreakMinutes(breakMin != null ? String(breakMin) : '');
      } else {
        if (tt) {
          setCrEditStartDate(extractDateFromISO(tt.start_time));
          setCrEditEndDate(extractDateFromISO(tt.end_time));
          setCrEditStartTime(extractTimeFromISO(tt.start_time));
          setCrEditEndTime(extractTimeFromISO(tt.end_time));
          setCrWorkAreaId((tt as any).work_area || null);
          setCrEditBreakMinutes(String(calcBreakMinutesNumber(tt)));
        } else {
          setCrEditStartDate(''); setCrEditEndDate('');
          setCrEditStartTime(''); setCrEditEndTime('');
          setCrWorkAreaId(null);
          setCrEditBreakMinutes('');
        }
        setCrEditNotes('');
      }
    } catch (error) {
      console.error('CR load =>', error);
      if (error instanceof HttpError) {
        if (!globalErrorHandler.handle(error)) { setCrViewError('Failed to load change request'); }
      } else { setCrViewError('Failed to load change request'); }
    } finally { setCrViewLoading(false); }
  };

  const closeCRViewModal = () => { setCrViewModalVisible(false); };

  return {
    crModalMode, crViewModalVisible, crViewLoading, crViewError,
    crExisting, setCrExisting,
    crEditMode, setCrEditMode, crEditError, setCrEditError,
    crConflicts, setCrConflicts, crSourceTT,
    crEditStartDate, setCrEditStartDate,
    crEditEndDate, setCrEditEndDate,
    crEditStartTime, setCrEditStartTime,
    crEditEndTime, setCrEditEndTime,
    crEditNotes, setCrEditNotes,
    crEditBreakMinutes, setCrEditBreakMinutes,
    crWorkAreaId, setCrWorkAreaId,
    openCRApprovedModal, handleReverseClick, openCRViewModal, closeCRViewModal,
  };
}
